import React, { useCallback, useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

interface SyncedTopScrollbarProps {
  /** The horizontally-scrolling element this scrollbar drives. */
  targetRef: React.RefObject<HTMLElement>
  className?: string
}

/**
 * A horizontal scrollbar rendered *above* a wide scroll container and kept in sync
 * with it, so you don't have to scroll to the bottom of a long table to find the
 * real one. Renders nothing when the target doesn't actually overflow.
 */
export const SyncedTopScrollbar: React.FC<SyncedTopScrollbarProps> = ({
  targetRef,
  className,
}) => {
  const barRef = useRef<HTMLDivElement>(null)
  // Each element writes to the other's scrollLeft, which makes the other emit a
  // scroll event of its own. Scroll events are dispatched on the next frame, not
  // synchronously, so a flag cleared right after the write would always be stale by
  // the time it's read — instead we mark *which* element is about to receive a
  // programmatic scroll and let that one event consume the mark. Without this the
  // echo can write a stale position back mid-drag and fight the user's scrolling.
  const programmatic = useRef({ bar: false, target: false })
  const [scrollWidth, setScrollWidth] = useState(0)
  const [clientWidth, setClientWidth] = useState(0)

  const measure = useCallback(() => {
    const target = targetRef.current
    if (!target) return
    setScrollWidth(target.scrollWidth)
    setClientWidth(target.clientWidth)
  }, [targetRef])

  // Track the target's size *and* its content's size — a role column added by a
  // re-render changes scrollWidth without changing the container's own box.
  useEffect(() => {
    const target = targetRef.current
    if (!target) return

    measure()

    const observer = new ResizeObserver(measure)
    observer.observe(target)
    const content = target.firstElementChild
    if (content) observer.observe(content)

    window.addEventListener('resize', measure)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [targetRef, measure])

  useEffect(() => {
    const target = targetRef.current
    const bar = barRef.current
    if (!target || !bar) return

    // Mirror the target's current position now that the bar exists.
    bar.scrollLeft = target.scrollLeft

    // Only flag a programmatic scroll when the value actually changes, since an
    // assignment that changes nothing emits no event to consume the flag.
    const copyTo = (to: HTMLElement, from: HTMLElement, key: 'bar' | 'target') => {
      if (to.scrollLeft === from.scrollLeft) return
      programmatic.current[key] = true
      to.scrollLeft = from.scrollLeft
    }

    const onTargetScroll = () => {
      if (programmatic.current.target) {
        programmatic.current.target = false
        return
      }
      copyTo(bar, target, 'bar')
    }

    const onBarScroll = () => {
      if (programmatic.current.bar) {
        programmatic.current.bar = false
        return
      }
      copyTo(target, bar, 'target')
    }

    target.addEventListener('scroll', onTargetScroll, { passive: true })
    bar.addEventListener('scroll', onBarScroll, { passive: true })
    return () => {
      target.removeEventListener('scroll', onTargetScroll)
      bar.removeEventListener('scroll', onBarScroll)
    }
    // Re-binds when the bar mounts or the content width changes.
  }, [targetRef, scrollWidth, clientWidth])

  if (scrollWidth <= clientWidth + 1) return null

  return (
    <div
      ref={barRef}
      aria-hidden
      className={cn(
        // min-height so the track is guaranteed room: the inner spacer is 1px tall
        // and engines differ on whether an auto-height box grows for a scrollbar.
        'min-h-[12px] overflow-x-scroll overflow-y-hidden always-show-scrollbar',
        className
      )}
    >
      <div style={{ width: scrollWidth, height: 1 }} />
    </div>
  )
}
